import { readsApi, writesApi } from './api';
import type { Order, OrderItem, OrderStatusHistory, PaginatedResponse } from '@apptypes/index';

export interface AdminOrderListParams {
  page?: number;
  limit?: number;
  status?: string;
  businessId?: string;
  branchId?: string;
  /** All orders placed by one customer — how finance reaches a trace without
   *  an order id in hand. */
  customerId?: string;
  /** Free text over order number / customer name / phone. */
  q?: string;
  /** ISO timestamp (inclusive lower bound on created_at). */
  from?: string;
  /** ISO timestamp (inclusive upper bound on created_at). */
  to?: string;
}

export const ordersService = {
  // Admin/internal order reads → FastAPI :8000
  list: (params?: AdminOrderListParams) =>
    readsApi.get<PaginatedResponse<Order>>('/admin/orders', params as Record<string, unknown>),

  get: (orderId: string) =>
    readsApi.get<Order>(`/admin/orders/${orderId}`),

  getItems: (orderId: string) =>
    readsApi.get<OrderItem[]>(`/admin/orders/${orderId}/items`),

  getTracking: (orderId: string) =>
    readsApi.get<OrderStatusHistory[]>(`/admin/orders/${orderId}/tracking`),

  // Business-scoped order management → Spring Boot :8080
  listBusinessOrders: (businessId: string, branchId: string, params?: { status?: string; page?: number; limit?: number }) =>
    writesApi.get<PaginatedResponse<Order>>(`/businesses/${businessId}/branches/${branchId}/orders`, params as Record<string, unknown>),

  getBusinessOrder: (businessId: string, branchId: string, orderId: string) =>
    writesApi.get<Order>(`/businesses/${businessId}/branches/${branchId}/orders/${orderId}`),

  acceptOrder: (businessId: string, branchId: string, orderId: string, preparationTimeMinutes?: number) =>
    writesApi.post(`/businesses/${businessId}/branches/${branchId}/orders/${orderId}/accept`, { preparationTimeMinutes }),

  declineOrder: (businessId: string, branchId: string, orderId: string, reason?: string) =>
    writesApi.post(`/businesses/${businessId}/branches/${branchId}/orders/${orderId}/decline`, { reason }),

  updateStatus: (businessId: string, branchId: string, orderId: string, status: string) =>
    writesApi.patch(`/businesses/${businessId}/branches/${branchId}/orders/${orderId}/status`, { status }),
};
